import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './lib/auth';
import { PermissionsProvider } from './lib/usePermissions';
import { healthScoreConfigApi } from './lib/api';
import { setHealthScoreConfig } from './lib/health';
import Layout from './components/Layout';
import LoginPage from './pages/LoginPage';
import AuthCallback from './pages/AuthCallback';
import OverviewPage from './pages/OverviewPage';
import StoresPage from './pages/StoresPage';
import StoreDashboardPage from './pages/StoreDashboardPage';
import AreaDashboardPage from './pages/AreaDashboardPage';
import UsersPage from './pages/UsersPage';
import SettingsPage from './pages/SettingsPage';
import ChangelogPage from './pages/ChangelogPage';
import RoadmapPage from './pages/RoadmapPage';
import InventoryPage from './pages/InventoryPage';
import MyKitPage from './pages/MyKitPage';
import InventoryItemPage from './pages/InventoryItemPage';
import AssistancePage from './pages/AssistancePage';
import TicketDetailPage from './pages/TicketDetailPage';
import StaffPage from './pages/StaffPage';
import StaffMemberPage from './pages/StaffMemberPage';
import StaffHomePage from './pages/StaffHomePage';
import MyProfilePage from './pages/MyProfilePage';
import LeaveRequestsPage from './pages/LeaveRequestsPage';
import NotificationsPage from './pages/NotificationsPage';
import OrdersPage from './pages/OrdersPage';
import TrainingPage from './pages/TrainingPage';
import PresentationsPage from './pages/PresentationsPage';
import { Toaster } from 'sonner';
import ErrorBoundary from './components/ErrorBoundary';
import './App.css';

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  // Bootstrap della config Health Score (semafori, soglie band, pesi KPI).
  // Lazy: parte solo dopo l'autenticazione perché l'endpoint richiede sessione.
  // Se l'OPS la modifica via SettingsPage, anche quella chiama
  // setHealthScoreConfig() col payload aggiornato → niente reload necessario.
  // OPS-only (2026-05-21 hardening Impostazioni). Per AM/SU il backend
  // restituisce 403; gli hook React mantengono i default hardcoded.
  useEffect(() => {
    if (!user) return;
    if (user.role !== 'operations_manager') return;
    healthScoreConfigApi.get()
      .then(r => setHealthScoreConfig(r.data))
      .catch(() => { /* tieni i default hardcoded */ });
  }, [user]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-8 h-8 border-2 border-black border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

// Guard per le route Impostazioni: tutto ciò che vive sotto /settings (e i
// redirect legacy /imports, /data-health, ecc.) deve essere accessibile solo
// agli Operations Manager. AM e Store User vengono rimandati in Panoramica
// (user request 2026-05-21: "solo OPS possono vedere e modificare dati di
// impostazioni ovunque"). NB: il backend rifiuta comunque le chiamate
// settings-api per i non-OPS, questo è il livello di hardening UI.
// Importante: il `ProtectedRoute` esterno garantisce che `user` non sia null
// quando si arriva qui (loading=false e user!=null), quindi il check è
// determinante e non lampeggia.
function OpsOnlyRoute({ children }) {
  const { user } = useAuth();
  if (!user || user.role !== 'operations_manager') {
    return <Navigate to="/" replace />;
  }
  return children;
}

// HomeByRole: routing della home `/` in base al ruolo:
//   - OPS / HR / Area Manager → Panoramica gestionale (KPI multi-store)
//   - Store Manager           → Home Staff personale (performance, ticket, agenda)
//   - Staff (store_user)      → redirect a "Il mio profilo" (è la voce in cima
//                              nella loro sidebar minimale — coerente con UX)
function HomeByRole() {
  const { user } = useAuth();
  if (user?.role === 'store_user') {
    return <Navigate to="/my-profile" replace />;
  }
  if (user?.role === 'store_manager') {
    return <StaffHomePage />;
  }
  return <OverviewPage />;
}

function AppRouter() {
  const location = useLocation();
  const navigate = useNavigate();

  // Service worker → main thread bridge: quando l'utente clicca su una
  // notifica push mentre l'app è già aperta, il SW invia `NAVIGATE` con
  // il link target. Lo usiamo per fare un client-side navigate (no full
  // reload) verso la pagina giusta.
  useEffect(() => {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return undefined;
    const handler = (event) => {
      if (event?.data?.type === 'NAVIGATE' && typeof event.data.link === 'string') {
        try { navigate(event.data.link); } catch { /* */ }
      }
    };
    navigator.serviceWorker.addEventListener('message', handler);
    return () => navigator.serviceWorker.removeEventListener('message', handler);
  }, [navigate]);

  // Check URL fragment for session_id synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      {/* Public — shareable link, no login required */}
      <Route path="/changelog" element={<ChangelogPage />} />
      <Route path="/roadmap" element={<RoadmapPage />} />
      <Route
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<HomeByRole />} />
        <Route path="stores" element={<StoresPage />} />
        <Route path="stores/:storeId" element={<StoreDashboardPage />} />
        <Route path="area-dashboard" element={<AreaDashboardPage />} />
        {/* Legacy routes — ora vivono come tab interni di Impostazioni
            (refactor 2026-05-08, idea_d61fb5573eba). Redirect 1:1 per
            non rompere bookmark/email vecchi. Tutte protette OpsOnly. */}
        <Route path="imports" element={<OpsOnlyRoute><Navigate to="/settings?tab=imports" replace /></OpsOnlyRoute>} />
        <Route path="data-health" element={<OpsOnlyRoute><Navigate to="/settings?tab=data-health" replace /></OpsOnlyRoute>} />
        <Route path="store-mappings" element={<OpsOnlyRoute><Navigate to="/settings?tab=mappings" replace /></OpsOnlyRoute>} />
        <Route path="audit-log" element={<OpsOnlyRoute><Navigate to="/settings?tab=audit" replace /></OpsOnlyRoute>} />
        <Route path="budgets" element={<Navigate to="/stores" replace />} />
        <Route path="costs" element={<Navigate to="/stores" replace />} />
        <Route path="users" element={<UsersPage />} />
        <Route path="inventory" element={<InventoryPage />} />
        <Route path="inventory/:itemId" element={<InventoryItemPage />} />
        <Route path="orders" element={<OrdersPage />} />
        <Route path="training" element={<TrainingPage />} />
        <Route path="presentations" element={<PresentationsPage />} />
        <Route path="my-kit" element={<MyKitPage />} />
        <Route path="staff" element={<StaffPage />} />
        <Route path="staff/:staffId" element={<StaffMemberPage />} />
        <Route path="leave" element={<LeaveRequestsPage />} />
        <Route path="notifications" element={<NotificationsPage />} />
        <Route path="my-profile" element={<MyProfilePage />} />
        <Route path="settings" element={<OpsOnlyRoute><SettingsPage /></OpsOnlyRoute>} />
      </Route>
      {/* Sezione Assistenza tecnica — stesso pattern di Changelog/Roadmap:
       *  layout autonomo (header dedicato, niente sidebar) ma sempre
       *  autenticato. Le pagine renderizzano AssistanceHeader al loro
       *  interno e si vestono come "mini-app dedicata". */}
      <Route
        path="assistance"
        element={<ProtectedRoute><AssistancePage /></ProtectedRoute>}
      />
      <Route
        path="assistance/:ticketId"
        element={<ProtectedRoute><TicketDetailPage /></ProtectedRoute>}
      />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <PermissionsProvider>
          <ErrorBoundary>
            <AppRouter />
          </ErrorBoundary>
          {/* Senza questo i toast (toast.success/toast.error) non compaiono:
              un salvataggio rifiutato dal server resterebbe senza feedback. */}
          <Toaster position="top-center" richColors closeButton duration={5000}
                   toastOptions={{ className: 'rounded-none' }} />
        </PermissionsProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
